import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import {
  cadastrosPendentesService,
  type CadastroPendente,
} from '@/services/cadastrosPendentesService';
import { formatPhone, formatCPF } from '@/utils/formatters';
import {
  ClipboardList,
  RefreshCw,
  Check,
  X,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Loader2,
  Send,
  Trash2,
} from 'lucide-react';

interface VendedorSimples {
  id: string;
  nome: string;
  email?: string;
  ativo: boolean;
}

function formatDate(s: string) {
  try {
    return new Date(s).toLocaleString('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return s;
  }
}

export default function Cadastros() {
  const [list, setList] = useState<CadastroPendente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [vendedorId, setVendedorId] = useState('');
  const [vendedores, setVendedores] = useState<VendedorSimples[]>([]);
  const [vendedoresLoading, setVendedoresLoading] = useState(false);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [resendEmailId, setResendEmailId] = useState<string | null>(null);
  const [excluirId, setExcluirId] = useState<string | null>(null);
  const { toast } = useToast();

  const loadPendentes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cadastrosPendentesService.listarPendentes();
      setList(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar cadastros');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendentes();
  }, [loadPendentes]);

  const loadVendedores = useCallback(async () => {
    setVendedoresLoading(true);
    try {
      const res = await fetchWithAuth('/api/parceiros/vendedores');
      if (!res.ok) throw new Error('Erro ao carregar vendedores');
      const data = await res.json();
      setVendedores(Array.isArray(data) ? data : []);
    } catch {
      setVendedores([]);
    } finally {
      setVendedoresLoading(false);
    }
  }, []);

  useEffect(() => {
    if (approveId) loadVendedores();
  }, [approveId, loadVendedores]);

  const handleAprovar = async () => {
    if (!approveId || !vendedorId) {
      toast({
        title: 'Selecione o vendedor responsável',
        variant: 'destructive',
      });
      return;
    }
    setActionLoading(true);
    try {
      const result = await cadastrosPendentesService.aprovar(approveId, vendedorId);
      if (result.email_enviado === false) {
        const motivo = result.email_erro
          ? `Motivo: ${result.email_erro}`
          : 'Configure SMTP no backend (variáveis SMTP_HOST, SMTP_USER, SMTP_PASS).';
        toast({
          title: 'Parceiro criado',
          description: `E-mail não enviado. ${motivo} O cadastro permanece na lista — use "Reenviar e-mail" até o envio ser confirmado.`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Cadastro aprovado',
          description: 'Parceiro criado e e-mail de boas-vindas enviado com credenciais de acesso.',
        });
      }
      setApproveId(null);
      setVendedorId('');
      loadPendentes();
    } catch (e) {
      toast({
        title: 'Erro ao aprovar',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleExcluir = async () => {
    if (!excluirId) return;
    setActionLoading(true);
    try {
      await cadastrosPendentesService.excluir(excluirId);
      toast({
        title: 'Cadastro excluído',
        description: 'O parceiro foi removido do sistema.',
      });
      setExcluirId(null);
      loadPendentes();
    } catch (e) {
      toast({
        title: 'Erro ao excluir',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReenviarEmail = async (id: string) => {
    setResendEmailId(id);
    try {
      const result = await cadastrosPendentesService.reenviarEmail(id);
      if (result.email_enviado) {
        toast({
          title: 'E-mail enviado',
          description: 'E-mail de boas-vindas enviado com sucesso. O cadastro foi concluído.',
        });
        loadPendentes();
      }
    } catch (e) {
      toast({
        title: 'Erro ao reenviar e-mail',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setResendEmailId(null);
    }
  };

  const handleRejeitar = async () => {
    if (!rejectId) return;
    setActionLoading(true);
    try {
      await cadastrosPendentesService.rejeitar(rejectId, motivoRejeicao || undefined);
      toast({
        title: 'Cadastro rejeitado',
      });
      setRejectId(null);
      setMotivoRejeicao('');
      loadPendentes();
    } catch (e) {
      toast({
        title: 'Erro ao rejeitar',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const cadastroDetail = detailId ? list.find((c) => c.id === detailId) : null;

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Cadastros</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Solicitações de cadastro de parceiros pela página inicial. Aprove ou rejeite e, ao aprovar, um e-mail de boas-vindas com credenciais será enviado.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadPendentes} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Cadastros pendentes
          </CardTitle>
          <CardDescription>
            Lista de solicitações aguardando análise. Ao aprovar, selecione o vendedor responsável; um e-mail de boas-vindas com senha provisória será enviado ao parceiro.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : list.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Nenhum cadastro pendente.</p>
          ) : (
            <div className="space-y-4">
              {list.map((c) => (
                <Card key={c.id} className="border">
                  <CardContent className="pt-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="grid gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{c.nome}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span>{c.email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span>{formatPhone(c.telefone)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>{c.cidade} – {c.estado}</span>
                        </div>
                        {c.cpf && (
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span>{formatCPF(c.cpf)}</span>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Solicitado em {formatDate(c.created_at)}
                        </p>
                        {c.status === 'aguardando_email' && (
                          <p className="text-sm text-amber-600 dark:text-amber-500 font-medium mt-2">
                            Parceiro criado — e-mail ainda não enviado. Use o botão abaixo para reenviar.
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0">
                        {c.status === 'aguardando_email' ? (
                          <>
                            <Button
                              size="sm"
                              variant="default"
                              className="gap-1"
                              onClick={() => handleReenviarEmail(c.id)}
                              disabled={resendEmailId === c.id}
                            >
                              {resendEmailId === c.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                              {resendEmailId === c.id ? ' Enviando...' : 'Reenviar e-mail'}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="gap-1"
                              onClick={() => setExcluirId(c.id)}
                              disabled={actionLoading}
                            >
                              <Trash2 className="h-4 w-4" />
                              Excluir
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="default"
                              className="gap-1"
                              onClick={() => setApproveId(c.id)}
                            >
                              <Check className="h-4 w-4" />
                              Aprovar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="gap-1"
                              onClick={() => setRejectId(c.id)}
                            >
                              <X className="h-4 w-4" />
                              Rejeitar
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDetailId(detailId === c.id ? null : c.id)}
                        >
                          Ver dados
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog aprovar: selecionar vendedor */}
      <Dialog open={!!approveId} onOpenChange={(open) => !open && setApproveId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprovar cadastro</DialogTitle>
            <DialogDescription>
              Selecione o vendedor responsável por este parceiro. Um e-mail de boas-vindas com credenciais será enviado; no primeiro acesso o parceiro fará login e definirá a senha permanente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Vendedor responsável *</Label>
            <Select
              value={vendedorId}
              onValueChange={setVendedorId}
              disabled={vendedoresLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={vendedoresLoading ? 'Carregando...' : 'Selecione o vendedor'} />
              </SelectTrigger>
              <SelectContent>
                {vendedores.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveId(null)} disabled={actionLoading}>
              Cancelar
            </Button>
            <Button onClick={handleAprovar} disabled={actionLoading || !vendedorId || vendedoresLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {actionLoading ? ' Aprovando...' : 'Aprovar e enviar e-mail'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog rejeitar */}
      <Dialog open={!!rejectId} onOpenChange={(open) => !open && setRejectId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar cadastro</DialogTitle>
            <DialogDescription>
              Opcionalmente informe o motivo da rejeição.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="motivo-rejeicao">Motivo (opcional)</Label>
            <Input
              id="motivo-rejeicao"
              value={motivoRejeicao}
              onChange={(e) => setMotivoRejeicao(e.target.value)}
              placeholder="Ex: documentação incompleta"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectId(null)} disabled={actionLoading}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleRejeitar} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {actionLoading ? ' Rejeitando...' : 'Rejeitar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog excluir (aguardando_email) */}
      <Dialog open={!!excluirId} onOpenChange={(open) => !open && setExcluirId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir cadastro</DialogTitle>
            <DialogDescription>
              O parceiro será removido do sistema e não receberá e-mail de boas-vindas. O registro sairá da lista. Deseja continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcluirId(null)} disabled={actionLoading}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleExcluir} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {actionLoading ? ' Excluindo...' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog ver dados completos */}
      <Dialog open={!!detailId} onOpenChange={(open) => !open && setDetailId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dados do cadastro</DialogTitle>
            <DialogDescription>Informações enviadas pelo solicitante.</DialogDescription>
          </DialogHeader>
          {cadastroDetail && (
            <div className="grid gap-3 text-sm">
              <div><span className="font-medium text-muted-foreground">Nome:</span> {cadastroDetail.nome}</div>
              <div><span className="font-medium text-muted-foreground">E-mail:</span> {cadastroDetail.email}</div>
              <div><span className="font-medium text-muted-foreground">Telefone:</span> {formatPhone(cadastroDetail.telefone)}</div>
              <div><span className="font-medium text-muted-foreground">Cidade / Estado:</span> {cadastroDetail.cidade} – {cadastroDetail.estado}</div>
              {cadastroDetail.cpf && <div><span className="font-medium text-muted-foreground">CPF:</span> {formatCPF(cadastroDetail.cpf)}</div>}
              <div><span className="font-medium text-muted-foreground">Data:</span> {formatDate(cadastroDetail.created_at)}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
